import type { Coding } from '@medplum/fhirtypes'
import type { SeenMedplumClient } from '@seen/core/client'
import { ICD10_SYSTEM, SEEN_HEALTH_SYSTEM } from '@seen/core/codeSystems'
import type { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import type { OrganizationConditionsList } from '@seen/core/models/organizationConditionsList'
import { getReferenceString } from '@seen/core/references'

const CUSTOM_CONDITION_TAG: Coding = {
  system: SEEN_HEALTH_SYSTEM,
  code: 'custom-condition',
}

// A resolved condition the user picked to replace an existing one with. Mirrors
// the data the add flow writes when a condition is first created, so a replace
// leaves the participant's condition as correct as a fresh add.
export interface ConditionReplacement {
  title: string
  icd10: { code: string; display?: string }[]
  // Whether the replacement was authored as a custom condition (tags the
  // resource so it stays distinguishable from coded picks, matching add).
  custom: boolean
  // When set, the replacement is also recorded on the org conditions list for
  // reuse — same side effect the add flow performs for newly-seen conditions.
  orgListEntry?: { title: string; icd10Codes: Coding[]; source?: 'custom' }
}

// Apply a replacement to an existing diagnosis in place. Overwrites the name and
// the full code (ICD-10 codings + text) so the display string can no longer
// drift from the underlying code, while leaving onset, clinical status, ordering
// and comments untouched.
export function applyConditionReplacement(
  target: Diagnosis,
  replacement: ConditionReplacement
): void {
  target.title = replacement.title
  target.code = {
    text: replacement.title,
    coding: replacement.icd10.map((c) => ({
      system: ICD10_SYSTEM,
      code: c.code,
      display: c.display,
    })),
  }
  if (replacement.custom) {
    target.addTag(CUSTOM_CONDITION_TAG)
  } else {
    target.removeTag(CUSTOM_CONDITION_TAG)
  }
}

// Record a replacement condition on the org conditions list so it becomes
// reusable org-wide, mirroring the add flow's org-list bookkeeping.
export function recordConditionOnOrgList(
  orgConditionsList: OrganizationConditionsList,
  entry: NonNullable<ConditionReplacement['orgListEntry']>,
  medplum: SeenMedplumClient
): void {
  const profile = medplum.getProfile()
  orgConditionsList.add({
    title: entry.title,
    icd10Codes: entry.icd10Codes,
    snomedCodes: [],
    addedOn: new Date().toISOString(),
    addedBy: profile
      ? { reference: getReferenceString(profile) }
      : { reference: 'Organization/seen-health' },
    source: entry.source,
  })
}
